import type { ForkDecision } from '@/lib/code-sessions/fork-policy';

import type { SessionTurnState } from './session-turn-state';

import { isPersonTurn, promptPreview, turnPromptText } from '@/data/code-sessions/turn-prompt';
import { canForkFromTurn, canForkSession } from '@/lib/code-sessions/fork-policy';

/** A prompt the Fork from message dialog offers. */
export type ForkableMessage = { turnId: string; text: string; createdAt: string };

// OpenCode's dialog shows each prompt on one line, cut at 200 characters.
const FORKABLE_TEXT_LENGTH = 200;

function busyOr(busyReason: string | undefined, decide: () => ForkDecision): ForkDecision {
  return busyReason === undefined ? decide() : { ok: false, reason: busyReason };
}

/** Fork from here on every visible saved turn, keyed by turn ID. */
export function messageForks(
  state: SessionTurnState,
  busyReason: string | undefined,
): Map<string, ForkDecision> {
  const forks = new Map<string, ForkDecision>();
  for (const turn of state.policy.turns) {
    if (!state.hidden.has(turn.id)) {
      forks.set(
        turn.id,
        busyOr(busyReason, () => canForkFromTurn(state.policy, turn)),
      );
    }
  }
  return forks;
}

/** Fork from the latest message; a conversation OpenCode has no message of cannot be forked. */
export function latestFork(state: SessionTurnState, busyReason: string | undefined): ForkDecision {
  return busyOr(busyReason, () => canForkFromTurn(state.policy));
}

/** Why no message can be picked; an empty conversation still opens the dialog. */
export function forkDialogReason(
  state: SessionTurnState,
  busyReason: string | undefined,
): string | undefined {
  const decision = busyOr(busyReason, () => canForkSession(state.policy));
  return decision.ok ? undefined : decision.reason;
}

/** The prompts a fork can start before, newest first, as OpenCode's Fork from message dialog lists them. */
export function forkableMessages(state: SessionTurnState): ForkableMessage[] {
  return state.turns
    .filter(
      (turn) => isPersonTurn(turn) && !state.hidden.has(turn.id) && !state.unsent.has(turn.id),
    )
    .reverse()
    .map((turn) => ({
      turnId: turn.id,
      text: promptPreview(turnPromptText(turn)).slice(0, FORKABLE_TEXT_LENGTH),
      createdAt: turn.createdAt,
    }));
}
